import React, { useEffect, useRef, useState } from "react";
import { Send, Phone, ChevronLeft, MoreVertical } from "lucide-react";
import { api } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { WaziChatAvatar } from "./Logo";

const TypingDots = () => (
  <div className="bg-white text-[#2D3748] p-3 rounded-2xl rounded-tl-none shadow-sm flex items-center gap-1 max-w-[60%]" data-testid="typing-indicator">
    <span className="typing-dot inline-block w-2 h-2 bg-[#718096] rounded-full" />
    <span className="typing-dot inline-block w-2 h-2 bg-[#718096] rounded-full" />
    <span className="typing-dot inline-block w-2 h-2 bg-[#718096] rounded-full" />
  </div>
);

/**
 * PhoneSimulator
 * Props:
 *  - role: 'painter' | 'customer'
 *  - sessionPrefix: string (used to namespace session id)
 *  - initialIntro: string (first message from Wazi)
 *  - quickReplies: optional array of suggested replies for the demo
 */
export const PhoneSimulator = ({ role = "painter", sessionPrefix = "demo", initialIntro = null, quickReplies = [] }) => {
  const [sessionId] = useState(`${sessionPrefix}-${role}-${Math.random().toString(36).slice(2, 8)}`);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [completed, setCompleted] = useState(false);
  const scroller = useRef(null);

  useEffect(() => {
    // kick off the conversation by sending empty
    (async () => {
      try {
        // attach pending referral code (if visitor came from a /u/:slug?ref=CODE link)
        if (role === "painter") {
          try {
            const ref = localStorage.getItem("wazi_ref");
            if (ref) {
              await api.post(`/public/sim/attach_ref?session_id=${encodeURIComponent(sessionId)}&ref=${encodeURIComponent(ref)}`);
            }
          } catch (e) { /* ignore */ }
        }
        setSending(true);
        const res = await api.post("/public/sim/message", { session_id: sessionId, role, text: "" });
        setMessages([{ from: "wazi", text: res.data.reply, ts: Date.now() }]);
        setSending(false);
      } catch (e) {
        setMessages([{ from: "wazi", text: initialIntro || "Hi! 👋 I'm Wazi.", ts: Date.now() }]);
        setSending(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const value = (text ?? input).trim();
    if (!value || sending) return;
    setMessages((m) => [...m, { from: "user", text: value, ts: Date.now() }]);
    setInput("");
    setSending(true);
    // GA: track when a tester first sends a real message
    if (messages.length === 1) trackEvent("sim_started", { role });
    try {
      const res = await api.post("/public/sim/message", { session_id: sessionId, role, text: value });
      // simulate slight typing delay
      setTimeout(() => {
        setMessages((m) => [...m, { from: "wazi", text: res.data.reply, ts: Date.now(), payload: res.data.payload }]);
        if (res.data.state && res.data.state.endsWith(":complete")) {
          setCompleted(true);
          trackEvent("sim_completed", { role, has_payload: !!res.data.payload });
        }
        setSending(false);
      }, 450);
    } catch (e) {
      setMessages((m) => [...m, { from: "wazi", text: "Hmm, network issue. Try again.", ts: Date.now() }]);
      setSending(false);
    }
  };

  const reset = async () => {
    setMessages([]);
    setCompleted(false);
    await api.post(`/public/sim/reset?session_id=${encodeURIComponent(sessionId)}`).catch(() => {});
    const res = await api.post("/public/sim/message", { session_id: sessionId, role, text: "" });
    setMessages([{ from: "wazi", text: res.data.reply, ts: Date.now() }]);
  };

  return (
    <div
      data-testid={`phone-simulator-${role}`}
      className="w-full max-w-[340px] mx-auto bg-[#1f1f23] rounded-[2.5rem] p-2.5 shadow-2xl"
      style={{ boxShadow: "0 30px 80px -20px rgba(45,55,72,0.35), 0 0 0 8px #2D3748" }}
    >
      <div className="bg-white rounded-[2rem] overflow-hidden flex flex-col h-[600px] relative">
        {/* notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-5 bg-[#1f1f23] rounded-b-2xl z-10" />
        {/* header */}
        <div className="bg-[#25D366] text-white px-4 pt-7 pb-3 flex items-center gap-3">
          <ChevronLeft className="w-5 h-5 opacity-80" />
          <div className="w-10 h-10 rounded-xl overflow-hidden">
            <WaziChatAvatar size={40} />
          </div>
          <div className="flex-1">
            <div className="font-heading font-semibold leading-tight">Wazi</div>
            <div className="text-xs opacity-80">online · typing for you</div>
          </div>
          <Phone className="w-5 h-5 opacity-90" />
          <MoreVertical className="w-5 h-5 opacity-90" />
        </div>
        {/* body */}
        <div ref={scroller} className="flex-1 wazi-chat-bg p-3 overflow-y-auto phone-scroll flex flex-col gap-2">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"} animate-message-in`}>
              <div
                className={`whitespace-pre-wrap text-sm leading-relaxed max-w-[80%] px-3 py-2 rounded-2xl shadow-sm ${
                  m.from === "user"
                    ? "bg-[#DCF8C6] text-[#2D3748] rounded-tr-none"
                    : "bg-white text-[#2D3748] rounded-tl-none"
                }`}
              >
                {m.text}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start animate-message-in">
              <TypingDots />
            </div>
          )}
        </div>
        {/* quick replies */}
        {quickReplies?.length > 0 && !completed && (
          <div className="px-2 pt-2 flex flex-wrap gap-2 bg-[#F7FAFC] border-t border-[#E2E8F0]">
            {quickReplies.map((qr, i) => (
              <button
                key={i}
                data-testid={`quick-reply-${i}`}
                onClick={() => send(qr)}
                className="text-xs px-3 py-1.5 bg-white border border-[#E2E8F0] rounded-full hover:border-[#25D366] hover:text-[#25D366] transition-colors"
              >
                {qr}
              </button>
            ))}
          </div>
        )}
        {/* input */}
        <div className="bg-[#F7FAFC] border-t border-[#E2E8F0] p-2 flex items-center gap-2">
          {completed ? (
            <button
              data-testid="sim-reset-btn"
              onClick={reset}
              className="w-full bg-[#1E88E5] hover:bg-[#1565C0] text-white py-2 rounded-full text-sm font-medium"
            >
              Try again
            </button>
          ) : (
            <>
              <input
                data-testid="sim-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Type a message…"
                className="flex-1 bg-white rounded-full px-4 py-2 text-sm border border-[#E2E8F0] focus:border-[#25D366] outline-none"
              />
              <button
                data-testid="sim-send-btn"
                onClick={() => send()}
                disabled={sending || !input.trim()}
                className="w-10 h-10 rounded-full bg-[#25D366] hover:bg-[#16A34A] text-white flex items-center justify-center disabled:opacity-40"
              >
                <Send className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhoneSimulator;
